import { api } from '../api/client';

import { API_MULTI_ADD_FORMS_URL } from '../utils';
import { withElementChoicesField } from '../utils/elementRefusal';

/**
 * Multi add forms for one collection: one form that creates SEVERAL items of
 * several cards in one submission — ordered steps (one card each, optionally
 * repeatable), ad hoc inputs, and per-element mappings. Two tiers, the same
 * split as quick add templates — `scope: 'user'` (personal, creator-only)
 * and `scope: 'collection'` (pinned for everyone, managed by managers).
 *
 * Wire shape (keepr-api docs/SCHEMA.md § Multi add forms):
 *   { _id, collection_id, scope, userId, name, linkItems,
 *     inputs: [{ key, label, dataType, options }],
 *     steps: [{ key, card_id, label, repeat: { min, max, label } | null,
 *               fields: { [element]: { from: 'omit' }
 *                                  | { from: 'static', value }
 *                                  | { from: 'input', input, transform }
 *                                  | { from: 'element', step, element, transform }
 *                                  | { from: 'step', step } } }], … }
 * An element ABSENT from `fields` is asked on the form.
 *
 * The form is a conduit: it stores nothing a user types. A submission
 * becomes items on the API, under the caller's own grants.
 */

const formUrl = (collectionId, formId) => `${API_MULTI_ADD_FORMS_URL(collectionId)}/${formId}`;

/**
 * The collection's own forms (collection tier first, then the caller's own,
 * name-sorted within each). A sub-collection inherits none from the
 * collections above it (owner ruling 2026-10-21, KPR-1084): the API's
 * `inherited` is always empty and is not read.
 */
export async function getMultiAddFormLists(collectionId) {
	const { data } = await api.get(API_MULTI_ADD_FORMS_URL(collectionId));
	return { forms: Array.isArray(data?.forms) ? data.forms : [] };
}

/** The collection's own forms only (what the managers edit). */
export async function getMultiAddForms(collectionId) {
	return (await getMultiAddFormLists(collectionId)).forms;
}

/** One visible form (a launch link, a reload). 404 when it is not the caller's to see. */
export async function getMultiAddForm(collectionId, formId) {
	const { data } = await api.get(formUrl(collectionId, formId));
	return data;
}

/**
 * Always creates in the USER tier — promotion is a separate, manage-gated
 * step. Needs write on every step's card; 409 on a duplicate name in the
 * tier; a 400 names `steps.<key>.fields.<element>` or `inputs.<key>` (an
 * unmapped input carries `code: 'input_unmapped'`).
 */
export async function createMultiAddForm(collectionId, { name, inputs, steps, linkItems, elementChoices = null }) {
	const payload = { name, steps };
	if (inputs !== undefined) payload.inputs = inputs;
	if (linkItems !== undefined) payload.linkItems = linkItems;
	// The person's answer to an ambiguous element name in a seed filter (utils/elementRefusal.js).
	const { data } = await api.post(API_MULTI_ADD_FORMS_URL(collectionId), withElementChoicesField(payload, elementChoices));
	return data;
}

/**
 * Partial update. Only the keys present are touched; a body change (inputs,
 * steps, linkItems) is re-validated whole on the API. `scope` is not
 * patchable — promote/demote are their own endpoints.
 */
export async function updateMultiAddForm(collectionId, formId, patch = {}) {
	const payload = {};
	['name', 'inputs', 'steps', 'linkItems'].forEach(key => {
		if (patch[key] !== undefined) payload[key] = patch[key];
	});
	const { data } = await api.patch(formUrl(collectionId, formId), withElementChoicesField(payload, patch.elementChoices));
	return data;
}

export async function deleteMultiAddForm(collectionId, formId) {
	await api.delete(formUrl(collectionId, formId));
}

/** manage — moves the form into the collection tier (creator kept as provenance). */
export async function promoteMultiAddForm(collectionId, formId) {
	const { data } = await api.post(`${formUrl(collectionId, formId)}/promote`);
	return data;
}

/** manage — pulls it back to the user tier, owned by the CALLER. */
export async function demoteMultiAddForm(collectionId, formId) {
	const { data } = await api.post(`${formUrl(collectionId, formId)}/demote`);
	return data;
}

/**
 * Submits one filled form: `{ inputs: { key: value }, steps: { key: [{ elements }] } }`
 * → `{ items: [{ step, index, id, card_id, displayValue }], linked? }`, or
 * `{ partial: true, items, failed: { step, index, message } }` when a write
 * failed after earlier rows landed. A 400 with `code: 'rows'` carries
 * `errors: [{ step, index, element, code, message }]` (`step` null for an
 * ad hoc input) and means NOTHING was written.
 */
/**
 * The lines a form OPENS with: one instance per item a seeded step's query
 * selects, pre-filled from that item. A read, never a write — and the API
 * runs every query under the caller's own grants, so a seeded line can only
 * ever be a row this person could already list.
 */
export async function seedMultiAddForm(collectionId, formId, inputs) {
	const { data } = await api.post(`${formUrl(collectionId, formId)}/seed`, { inputs: inputs || {} });
	return data || { steps: {}, warnings: [] };
}

/**
 * `config` is the request's own (`{ headers: { 'X-Attestation': token } }`):
 * a submit that writes a card asking for a signature answers 428
 * `attestation_required` with `mode`, `collection_id`, `card_id` and
 * `card_ids` before writing anything, and the page resends the SAME body with
 * the signature (KPR-1725).
 */
export async function submitMultiAddForm(collectionId, formId, body, config) {
	const { data } = await api.post(`${formUrl(collectionId, formId)}/submit`, body, config);
	return data || { items: [] };
}
